import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createRequestLoad,
  updateSingleReadingMode,
} from './kioskApi';
import {
  EMPTY_REQUEST_FORM,
} from './kioskConstants';
import { useAuth } from '../../../shared/auth/AuthContext';
import { getUserAvatarLabel } from '../../../shared/auth/avatarLabel';
import { getUserDisplayName } from '../../../shared/auth/userDisplayName';
import { KioskBookingsPanel } from './KioskBookingsPanel';
import { KioskCurrentWeighingPanel } from './KioskCurrentWeighingPanel';
import { KioskHero } from './KioskHero';
import { InboundQuantityMismatchModal } from './InboundQuantityMismatchModal';
import { KioskStationControls } from './KioskStationControls';
import { LoadDetailModal } from './LoadDetailModal';
import { RequestLoadModal } from './RequestLoadModal';
import {
  buildDeviceMap,
  buildRequestLoadName,
  calculateSuggestedReturnQuantityPacks,
  createEmptyRequestLoadForm,
  mergeRequestLoad,
} from './kioskTransforms';
import {
  loadStoredSelectedRegisterId,
} from './kioskStorage';
import { useRequestLoadSummary } from './useRequestLoadSummary';
import { useKioskSetupData } from './useKioskSetupData';
import { useCurrentKioskRequest } from './useCurrentKioskRequest';
import { useKioskLoadModel } from './useKioskLoadModel';
import { useKioskRealtime } from './useKioskRealtime';
import { useKioskFormSync } from './useKioskFormSync';
import { useSelectedKioskReadings } from './useSelectedKioskReadings';
import { useKioskLoadDetail } from './useKioskLoadDetail';
import { useKioskReadingActions } from './useKioskReadingActions';
import { useKioskRequestWorkflow } from './useKioskRequestWorkflow';
import './KioskPage.css';

export default function KioskPage() {
  const { user } = useAuth();
  const currentActorName = getUserDisplayName(user);
  const avatarLabel = getUserAvatarLabel(user);
  const {
    bindings,
    devices,
    loadState,
    refreshVisibleData,
    requestLoads,
    requests,
    setBindings,
    setRequestLoads,
    setRequests,
    visibilityRefreshTick,
  } = useKioskSetupData();
  const [selectedRegisterId, setSelectedRegisterId] = useState(() => loadStoredSelectedRegisterId());
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [requestLoadActionState, setRequestLoadActionState] = useState({ type: 'idle', message: '' });
  const [singleReadingModeState, setSingleReadingModeState] = useState({ type: 'idle', message: '' });
  const [isRequestLoadModalOpen, setIsRequestLoadModalOpen] = useState(false);
  const [requestRefreshTick, setRequestRefreshTick] = useState(0);
  const [requestLoadModalMode, setRequestLoadModalMode] = useState('create');
  const [acceptLoadId, setAcceptLoadId] = useState('');
  const [selectedRequestLoadId, setSelectedRequestLoadId] = useState('');
  const [operatorForm, setOperatorForm] = useState({
    operatorName: '',
    confirmationNotes: '',
    returnQuantityPacks: '',
  });
  const [createForm, setCreateForm] = useState(EMPTY_REQUEST_FORM);
  const [createRequestDirection, setCreateRequestDirection] = useState('Outbound');
  const createRequestDirectionRef = useRef('Outbound');
  const returnQuantityAutoDefaultRef = useRef('');
  const [requestLoadForm, setRequestLoadForm] = useState(() => createEmptyRequestLoadForm());
  const operatorActorName = operatorForm.operatorName.trim() || currentActorName || 'kiosk-user';
  const refreshCurrentRequest = useCallback(() => {
    setRequestRefreshTick((current) => current + 1);
  }, []);
  const {
    currentRequest,
    currentRequestLoad,
    readings,
    readingsState,
    requestState,
    setCurrentRequest,
    setCurrentRequestLoad,
    setReadings,
    setReadingsState,
    setRequestState,
  } = useCurrentKioskRequest({
    requestRefreshTick,
    selectedRegisterId,
    setRequestLoads,
    visibilityRefreshTick,
  });

  const deviceMap = useMemo(() => buildDeviceMap(devices), [devices]);
  const selectedBinding = useMemo(
    () => bindings.find((binding) => binding.registerId === selectedRegisterId) ?? null,
    [bindings, selectedRegisterId],
  );
  const activeBindings = useMemo(
    () => bindings.filter((binding) => binding.status === 'Active'),
    [bindings],
  );
  const {
    availableOutboundRequestLoads,
    canCreateFromCurrentRequest,
    currentRequestForSelectedRegister,
    displayedRequestLoad,
    displayedRequestLoadName,
    displayedRequestLoadRequests,
    inboundRequestLoadPrefix,
    openRequestLoads,
    recentOpenRequestLoads,
    registerOperationBlocker,
  } = useKioskLoadModel({
    createForm,
    currentRequest,
    currentRequestLoad,
    deviceMap,
    requestLoadForm,
    requestLoads,
    requests,
    selectedBinding,
    selectedRegisterId,
    selectedRequestLoadId,
  });

  const requestLoadSummary = useRequestLoadSummary(displayedRequestLoad, displayedRequestLoadRequests, deviceMap);

  useKioskRealtime({
    currentRequest,
    refreshVisibleData,
    selectedRegisterId,
    setConnectionStatus,
    setCurrentRequest,
    setCurrentRequestLoad,
    setReadings,
    setReadingsState,
    setRequestLoads,
    setRequestState,
    setRequests,
    setSelectedRequestLoadId,
  });
  useEffect(() => {
    if (
      !isRequestLoadModalOpen
      || requestLoadModalMode !== 'accept'
      || acceptLoadId
      || openRequestLoads.length === 0
    ) {
      return;
    }

    setAcceptLoadId(openRequestLoads[0].loadId);
  }, [acceptLoadId, isRequestLoadModalOpen, openRequestLoads, requestLoadModalMode]);

  useKioskFormSync({
    activeBindings,
    availableOutboundRequestLoads,
    bindings,
    createRequestDirectionRef,
    currentActorName,
    currentRequest,
    requestLoadForm,
    selectedRegisterId,
    setCreateRequestDirection,
    setOperatorForm,
    setRequestLoadForm,
    setSelectedRegisterId,
    setSelectedRequestLoadId,
  });

  const canConfirm = Boolean(
    currentRequest
      && currentRequest.requestStatus !== 'Completed'
      && currentRequest.requestStatus !== 'Cancelled'
      && currentRequest.selectedScannerReadingId
      && currentRequest.selectedScaleReadingId,
  );

  const canReject = Boolean(
    currentRequest
      && currentRequest.requestStatus !== 'Cancelled'
      && currentRequest.confirmationStatus !== 'Confirmed'
      && currentRequest.confirmationStatus !== 'Rejected',
  );

  const displayedCurrentRequest = currentRequestForSelectedRegister;
  const selectedRequestDirection = createRequestDirection === 'Inbound' ? 'Inbound' : 'Outbound';
  const replacedFromLoadName = useMemo(() => {
    if (!displayedCurrentRequest?.replacesRequestId) return null;
    if (displayedCurrentRequest.replacedFromLoadName) return displayedCurrentRequest.replacedFromLoadName;
    const voidedRequest = requests.find((r) => r.requestId === displayedCurrentRequest.replacesRequestId);
    if (!voidedRequest?.requestLoadId) return null;
    return requestLoads.find((l) => l.loadId === voidedRequest.requestLoadId)?.name ?? voidedRequest.requestLoadId;
  }, [displayedCurrentRequest?.replacedFromLoadName, displayedCurrentRequest?.replacesRequestId, requests, requestLoads]);
  const displayedReadings = displayedCurrentRequest ? readings : [];
  const displayedReadingsState = displayedCurrentRequest
    ? readingsState
    : { type: 'idle', message: '' };
  const {
    handleCameraQrDetected,
    handleManualWeightSubmit,
    handleQrWeightLookup,
  } = useKioskReadingActions({
    currentRequest: displayedCurrentRequest,
    refreshCurrentRequest,
    setReadings,
    setReadingsState,
  });
  const {
    closeLoadDetailModal,
    detailLoad,
    detailLoadId,
    detailLoadState,
    detailLoadSummary,
    openLoadDetailModal,
    updateDetailLoadStatus,
    voidDetailRequest,
  } = useKioskLoadDetail({
    createRequestDirectionRef,
    operatorActorName,
    operatorSource: currentActorName ? `operator:${currentActorName}` : 'operator:kiosk-user',
    setCreateRequestDirection,
    setCurrentRequest,
    setReadings,
    setRequestLoads,
    setRequestState,
    setRequests,
  });
  const displayedCanConfirm = Boolean(displayedCurrentRequest && canConfirm);
  const displayedCanReject = Boolean(displayedCurrentRequest && canReject);
  const disableDecisionButtons = Boolean(
    displayedCurrentRequest
      && displayedReadings.length === 0
      && !displayedCurrentRequest.requestLoadId
      && !selectedRequestLoadId,
  );
  const displayedCurrentRequestSummary = useMemo(
    () => requestLoadSummary.requests.find((request) => request.requestId === displayedCurrentRequest?.requestId) ?? null,
    [displayedCurrentRequest?.requestId, requestLoadSummary.requests],
  );
  const {
    scannerErrorMessage,
    scannerProductLabel: latestScannerProductLabel,
    scannerReading: latestScannerReading,
    scannerStoredQuantity: latestScannerStoredQuantity,
    scaleReading: latestScaleReading,
  } = useSelectedKioskReadings({
    deviceMap,
    displayedReadings,
    requestSummary: displayedCurrentRequestSummary,
    selectedBinding,
  });
  const suggestedReturnQuantityPacks = useMemo(() => {
    if (displayedCurrentRequest?.requestDirection !== 'Inbound') {
      return null;
    }

    return calculateSuggestedReturnQuantityPacks({
      availableQuantityPacks: latestScannerStoredQuantity,
      loadProducts: displayedRequestLoad?.aggregate?.products,
      scannedProduct: latestScannerReading?.scannedProduct,
    });
  }, [
    displayedCurrentRequest?.requestDirection,
    displayedRequestLoad?.aggregate?.products,
    latestScannerReading?.scannedProduct,
    latestScannerStoredQuantity,
  ]);
  const hasAssignedLoadDetails = Boolean(
    displayedCurrentRequest?.requestLoadId
      && currentRequestLoad?.loadId === displayedCurrentRequest.requestLoadId,
  );
  const inboundScaleReadingValue = String(latestScaleReading?.readingValue ?? '').trim();
  const hasInboundScaleWeight = inboundScaleReadingValue !== ''
    && Number.isFinite(Number(inboundScaleReadingValue));
  const defaultReturnQuantityPacks = suggestedReturnQuantityPacks
    ?? (hasAssignedLoadDetails && hasInboundScaleWeight ? latestScannerStoredQuantity : null);

  useEffect(() => {
    if (
      displayedCurrentRequest?.requestDirection !== 'Inbound'
      || !displayedCurrentRequest.selectedScannerReadingId
      || defaultReturnQuantityPacks == null
    ) {
      return;
    }

    const nextDefault = String(defaultReturnQuantityPacks);
    setOperatorForm((current) => {
      const mayReplaceCurrentValue = !current.returnQuantityPacks
        || current.returnQuantityPacks === returnQuantityAutoDefaultRef.current;
      returnQuantityAutoDefaultRef.current = nextDefault;
      return mayReplaceCurrentValue
        ? { ...current, returnQuantityPacks: nextDefault }
        : current;
    });
  }, [
    displayedCurrentRequest?.requestDirection,
    displayedCurrentRequest?.selectedScannerReadingId,
    defaultReturnQuantityPacks,
  ]);

  const {
    cancelQuantityMismatchConfirmation,
    confirmCurrentRequestWithLoad,
    confirmState,
    continueQuantityMismatchConfirmation,
    createState,
    createStationRequest,
    handleConfirmRequest,
    handleCreateRequest,
    handleRejectRequest,
    quantityMismatch,
    rejectState,
    resetConfirmationState,
  } = useKioskRequestWorkflow({
    closeRequestLoadModal,
    createRequestDirection,
    createRequestDirectionRef,
    currentRequest,
    openAcceptLoadModal,
    operatorActorName,
    operatorForm,
    operatorSource: currentActorName ? `operator:${currentActorName}` : 'operator:kiosk-user',
    registerOperationBlocker,
    requestLoads,
    requestRefreshTick,
    scannedProduct: latestScannerReading?.scannedProduct,
    scannedQuantityPacks: latestScannerStoredQuantity,
    selectedRegisterId,
    selectedRequestLoadId,
    setCurrentRequest,
    setReadings,
    setRequestLoadActionState,
    setRequestLoads,
    setRequestState,
    setRequests,
    visibilityRefreshTick,
  });

  const isPageLoading = loadState.type === 'pending' || requestState.type === 'pending';
  const pageLoadingMessage = loadState.type === 'pending'
    ? (loadState.message || 'Loading kiosk data...')
    : (requestState.message || 'Loading current request...');

  useEffect(() => {
    if (
      selectedRegisterId
      && !currentRequestForSelectedRegister
      && requestState.type === 'success'
      && createState.type === 'idle'
      && !registerOperationBlocker
    ) {
      createStationRequest({
        pendingMessage: 'Preparing station for the next reading...',
        successMessage: 'Station is ready for scanner and scale readings.',
        requestDirection: createRequestDirection,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegisterId, currentRequestForSelectedRegister?.requestId, requestState.type, createState.type, createRequestDirection, registerOperationBlocker]);

  function handleOperatorFieldChange(event) {
    const { name, value } = event.target;
    setOperatorForm((current) => ({ ...current, [name]: value }));
  }

  function applySelectedRequestDirection(nextDirection) {
    createRequestDirectionRef.current = nextDirection;
    setCreateRequestDirection(nextDirection);

    if (!currentRequestForSelectedRegister?.requestId) {
      return;
    }

    setCurrentRequest((current) => {
      if (!current || current.requestId !== currentRequestForSelectedRegister.requestId) {
        return current;
      }

      return {
        ...current,
        requestDirection: nextDirection,
      };
    });
    setRequests((currentItems) => currentItems.map((request) => (
      request.requestId === currentRequestForSelectedRegister.requestId
        ? { ...request, requestDirection: nextDirection }
        : request
    )));
  }

  async function handleSingleReadingModeToggle(event) {
    const enabled = event.target.checked;
    setSingleReadingModeState({ type: 'pending', message: '' });
    try {
      const updated = await updateSingleReadingMode(selectedRegisterId, enabled);
      setBindings((current) => current.map((b) => b.registerId === updated.registerId ? updated : b));
      setSingleReadingModeState({ type: 'idle', message: '' });
    } catch (error) {
      setSingleReadingModeState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to update single reading mode.',
      });
    }
  }

  function handleRequestLoadFormFieldChange(event) {
    const { name, value } = event.target;
    if (name === 'direction') {
      const nextDirection = value === 'Inbound' ? 'Inbound' : 'Outbound';
      applySelectedRequestDirection(nextDirection);
      setRequestLoadForm((current) => ({
        ...current,
        direction: nextDirection,
        linkedOutboundLoadId: nextDirection === 'Inbound' ? current.linkedOutboundLoadId : '',
      }));
      return;
    }

    setRequestLoadForm((current) => {
      if (name === 'name' && current.direction === 'Inbound') {
        if (!inboundRequestLoadPrefix) {
          return { ...current, name: value };
        }

        const prefixedValue = `${inboundRequestLoadPrefix} - `;
        let nextValue = value;
        if (value === inboundRequestLoadPrefix) {
          nextValue = '';
        } else if (value.startsWith(prefixedValue)) {
          nextValue = value.slice(prefixedValue.length);
        } else if (value.startsWith(inboundRequestLoadPrefix)) {
          nextValue = value.slice(inboundRequestLoadPrefix.length).replace(/^ - /, '').trimStart();
        }

        return { ...current, name: nextValue };
      }

      return { ...current, [name]: value };
    });
  }

  function openAcceptLoadModal() {
    setRequestLoadActionState({ type: 'idle', message: '' });
    setRequestLoadForm(createEmptyRequestLoadForm(
      currentActorName,
      createRequestDirectionRef.current,
    ));
    setRequestLoadModalMode('accept');
    setAcceptLoadId(openRequestLoads[0]?.loadId ?? '');
    setIsRequestLoadModalOpen(true);
  }

  function closeRequestLoadModal() {
    setIsRequestLoadModalOpen(false);
    setRequestLoadForm(createEmptyRequestLoadForm(
      currentActorName,
      createRequestDirectionRef.current,
    ));
    setRequestLoadModalMode('create');
    setAcceptLoadId('');
  }

  async function handleCreateRequestLoad(event) {
    event.preventDefault();

    if (registerOperationBlocker) {
      setRequestLoadActionState({ type: 'error', message: registerOperationBlocker });
      return;
    }

    const direction = requestLoadForm.direction === 'Inbound' ? 'Inbound' : 'Outbound';
    const linkedOutboundLoadId = direction === 'Inbound'
      ? requestLoadForm.linkedOutboundLoadId.trim()
      : '';
    const createdBy = requestLoadForm.createdBy.trim() || operatorActorName;
    const notes = requestLoadForm.notes.trim();
    const payload = {
      registerId: selectedRegisterId,
      name: buildRequestLoadName(requestLoadForm, linkedOutboundRequestLoad),
      direction,
      linkedOutboundLoadId: linkedOutboundLoadId || null,
      createdBy: createdBy || null,
      notes: notes || null,
    };

    if (!payload.name) {
      setRequestLoadActionState({ type: 'error', message: 'Request load name is required.' });
      return;
    }

    if (direction === 'Inbound' && !linkedOutboundLoadId) {
      setRequestLoadActionState({ type: 'error', message: 'Select the outbound load linked to this inbound load.' });
      return;
    }

    if (direction === 'Inbound' && matchedOutboundLoadIds.has(linkedOutboundLoadId)) {
      setRequestLoadActionState({
        type: 'error',
        message: 'This outbound load already has an inbound match. Select a different outbound load.',
      });
      return;
    }

    setRequestLoadActionState({ type: 'pending', message: 'Creating request load...' });

    try {
      const created = await createRequestLoad(payload, selectedRegisterId);
      setRequestLoads((currentItems) => mergeRequestLoad(currentItems, created));
      setCreateForm({ requestLoadId: created.loadId });
      setCurrentRequestLoad(created);
      setSelectedRequestLoadId(created.loadId);
      setRequestLoadActionState({
        type: 'success',
        message: `รอบการขนส่ง '${created.name}' พร้อมแล้วสำหรับการชั่งถัดไป.`,
      });
      if (requestLoadModalMode === 'accept') {
        await confirmCurrentRequestWithLoad(created.loadId, { requestLoadOverride: created });
      } else {
        closeRequestLoadModal();
      }
    } catch (error) {
      setRequestLoadActionState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถสร้างรอบการขนส่งได้.',
      });
    }
  }

  function handleSelectRequestLoad(requestLoad) {
    if (!requestLoad?.loadId || !currentRequestForSelectedRegister) {
      return;
    }

    setSelectedRequestLoadId(requestLoad.loadId);
    setAcceptLoadId(requestLoad.loadId);
    setCurrentRequestLoad(requestLoad);
    setCreateForm({ requestLoadId: requestLoad.loadId });
    resetConfirmationState();
  }

  async function handleAssignExistingLoadAndAccept() {
    if (!acceptLoadId) {
      setRequestLoadActionState({ type: 'error', message: 'Select a load before accepting the request.' });
      return;
    }

    await confirmCurrentRequestWithLoad(acceptLoadId);
  }

  return (
    <section className="kiosk-page">
      <KioskHero
        eyebrow="สถานีชั่งน้ำหนัก"
        title="สถานีชั่งน้ำหนัก"
        avatarLabel={avatarLabel}
        loadState={loadState}
        connectionStatus={connectionStatus}
        showConnectionStatus
      />

      {isPageLoading ? (
        <div className="kiosk-page-loading" role="status" aria-live="polite">
          <div className="kiosk-loading-spinner" aria-hidden="true" />
          <h2>กำลังโหลดหน้าสถานี</h2>
          <p>{pageLoadingMessage}</p>
        </div>
      ) : (
        <div className="kiosk-grid">
          <div className="kiosk-stack">
            <KioskStationControls
              activeBindings={activeBindings}
              selectedRegisterId={selectedRegisterId}
              selectedBinding={selectedBinding}
              operatorName={operatorForm.operatorName}
              singleReadingModeState={singleReadingModeState}
              onRegisterChange={(event) => setSelectedRegisterId(event.target.value)}
              onSingleReadingModeChange={handleSingleReadingModeToggle}
            />
            <KioskBookingsPanel
              canSelectLoad={Boolean(currentRequestForSelectedRegister)}
              currentRequestLoadId={currentRequestForSelectedRegister?.requestLoadId}
              onOpenDetail={openLoadDetailModal}
              onSelectLoad={handleSelectRequestLoad}
              recentOpenRequestLoads={recentOpenRequestLoads}
              registerOperationBlocker={registerOperationBlocker}
              requests={requests}
              selectedRequestDirection={selectedRequestDirection}
              selectedRequestLoadId={selectedRequestLoadId}
              showRegisterBlocker={canCreateFromCurrentRequest}
            />
          </div>
          <KioskCurrentWeighingPanel
            confirmState={confirmState}
            createState={createState}
            deviceMap={deviceMap}
            disableDecisionButtons={disableDecisionButtons}
            displayedCanConfirm={displayedCanConfirm}
            displayedCanReject={displayedCanReject}
            displayedCurrentRequest={displayedCurrentRequest}
            displayedReadings={displayedReadings}
            displayedReadingsState={displayedReadingsState}
            displayedRequestLoad={displayedRequestLoad}
            latestScaleReading={latestScaleReading}
            scannerErrorMessage={scannerErrorMessage}
            latestScannerProductLabel={latestScannerProductLabel}
            latestScannerReading={latestScannerReading}
            latestScannerStoredQuantity={latestScannerStoredQuantity}
            onCameraQrDetected={handleCameraQrDetected}
            onManualWeightSubmit={handleManualWeightSubmit}
            onQrWeightLookup={handleQrWeightLookup}
            onConfirm={handleConfirmRequest}
            onCreateRequest={handleCreateRequest}
            onNotesChange={handleOperatorFieldChange}
            onReturnQuantityChange={handleOperatorFieldChange}
            onOpenDetail={openLoadDetailModal}
            onReject={handleRejectRequest}
            operatorNotes={operatorForm.confirmationNotes}
            returnQuantityPacks={operatorForm.returnQuantityPacks}
            returnQuantityPacksMax={latestScannerStoredQuantity}
            registerOperationBlocker={registerOperationBlocker}
            rejectState={rejectState}
            replacedFromLoadName={replacedFromLoadName}
            requests={requests}
            selectedBinding={selectedBinding}
            selectedRegisterId={selectedRegisterId}
            selectedRequestDirection={selectedRequestDirection}
            selectedRequestLoadId={selectedRequestLoadId}
          />

          <LoadDetailModal
            detailLoadId={detailLoadId}
            detailLoad={detailLoad}
            detailLoadState={detailLoadState}
            detailLoadSummary={detailLoadSummary}
            requests={requests}
            requestLoads={requestLoads}
            onUpdateStatus={updateDetailLoadStatus}
            onVoidRequest={voidDetailRequest}
            onClose={closeLoadDetailModal}
          />
          <RequestLoadModal
            isOpen={isRequestLoadModalOpen}
            mode={requestLoadModalMode}
            selectedRegisterId={selectedRegisterId}
            latestScannerReading={latestScannerReading}
            latestScaleReading={latestScaleReading}
            openRequestLoads={openRequestLoads}
            acceptLoadId={acceptLoadId}
            onAcceptLoadChange={setAcceptLoadId}
            confirmState={confirmState}
            onAssignExistingLoadAndAccept={handleAssignExistingLoadAndAccept}
            requestLoadForm={requestLoadForm}
            onRequestLoadFormChange={handleRequestLoadFormFieldChange}
            availableOutboundRequestLoads={availableOutboundRequestLoads}
            displayedRequestLoadName={displayedRequestLoadName}
            requestLoadActionState={requestLoadActionState}
            registerOperationBlocker={registerOperationBlocker}
            onSubmit={handleCreateRequestLoad}
            onClose={closeRequestLoadModal}
          />

          <InboundQuantityMismatchModal
            mismatch={quantityMismatch}
            onCancel={cancelQuantityMismatchConfirmation}
            onContinue={continueQuantityMismatchConfirmation}
          />

        </div>
      )}
    </section>
  );
}

